import { BrowserModule, Title } from "@angular/platform-browser";
import { inject, LOCALE_ID, NgModule } from "@angular/core";
import {
  TranslateModule,
  TranslateService,
  provideTranslateService,
} from "@ngx-translate/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { registerLocaleData } from "@angular/common";
import en from "@angular/common/locales/en";
import localeSq from "@angular/common/locales/sq";
import { environment } from "../environments/environment";
import { SharedModule } from "./@components/shared.module";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { createClient } from "graphql-ws";
import { provideHttpClient } from "@angular/common/http";
import {
  ar_EG,
  de_DE,
  en_US,
  es_ES,
  fr_FR,
  hy_AM,
  ja_JP,
  ko_KR,
  NZ_DATE_LOCALE,
  NZ_I18N,
  pt_PT,
  ro_RO,
  ru_RU,
  zh_CN,
} from "ng-zorro-antd/i18n";
import { TimeagoModule } from "ngx-timeago";
import { NzIconModule } from "ng-zorro-antd/icon";
import {
  UserOutline,
  BarChartOutline,
  ContainerOutline,
  CustomerServiceOutline,
  GlobalOutline,
  BulbOutline,
  NotificationOutline,
  BankOutline,
  CarOutline,
  FundOutline,
  ControlOutline,
  PlusOutline,
  DashboardOutline,
  MenuFoldOutline,
  LogoutOutline,
  MenuUnfoldOutline,
  ArrowLeftOutline,
  DeleteOutline,
  PictureTwoTone,
  ExportOutline,
  InboxOutline,
  DownloadOutline,
  LockOutline,
  EyeInvisibleOutline,
  FieldTimeOutline,
  ScheduleOutline,
  FolderOpenOutline,
  AlertOutline,
  MoreOutline,
  DollarOutline,
  MoneyCollectOutline,
  AccountBookOutline,
  ApartmentOutline,
  FileDoneOutline,
  FileProtectOutline,
  IdcardOutline,
  MailOutline,
  PhoneOutline,
  ProfileOutline,
  SaveOutline,
  StarFill,
  ThunderboltOutline,
  WalletOutline,
  TagsOutline,
  EnvironmentOutline,
  CreditCardOutline,
  CheckCircleOutline,
  BranchesOutline,
  HourglassOutline,
  ReloadOutline,
  RiseOutline,
  SettingOutline,
  UserSwitchOutline,
  WarningOutline,
  ApiOutline,
  ArrowRightOutline,
  InfoCircleOutline,
  KeyOutline,
  MinusCircleOutline,
  RadarChartOutline,
  SafetyCertificateOutline,
  StopOutline,
} from "@ant-design/icons-angular/icons";
import { IconDefinition } from "@ant-design/icons-angular";
import {
  enUS,
  es,
  fr,
  de,
  ar,
  hy,
  ko,
  ru,
  zhCN,
  ja,
  pt,
  ro,
  sq,
} from "date-fns/locale";
import { provideApollo } from "apollo-angular";
import { HttpLink } from "apollo-angular/http";
import { InMemoryCache } from "@apollo/client/cache";
import { ApolloLink, DefaultOptions, split } from "@apollo/client/core";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";

const icons: IconDefinition[] = [
  UserOutline,
  BarChartOutline,
  ContainerOutline,
  CustomerServiceOutline,
  GlobalOutline,
  BulbOutline,
  NotificationOutline,
  BankOutline,
  CarOutline,
  FundOutline,
  ControlOutline,
  PlusOutline,
  DashboardOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  LogoutOutline,
  ArrowLeftOutline,
  DeleteOutline,
  PictureTwoTone,
  ExportOutline,
  InboxOutline,
  DownloadOutline,
  LockOutline,
  MoreOutline,
  EyeInvisibleOutline,
  FieldTimeOutline,
  ScheduleOutline,
  FolderOpenOutline,
  DollarOutline,
  MoneyCollectOutline,
  AlertOutline,
  AccountBookOutline,
  ApartmentOutline,
  FileDoneOutline,
  FileProtectOutline,
  IdcardOutline,
  MailOutline,
  PhoneOutline,
  ProfileOutline,
  SaveOutline,
  StarFill,
  ThunderboltOutline,
  WalletOutline,
  TagsOutline,
  EnvironmentOutline,
  CreditCardOutline,
  CheckCircleOutline,
  BranchesOutline,
  HourglassOutline,
  ReloadOutline,
  RiseOutline,
  SettingOutline,
  UserSwitchOutline,
  WarningOutline,
  ApiOutline,
  ArrowRightOutline,
  InfoCircleOutline,
  KeyOutline,
  MinusCircleOutline,
  RadarChartOutline,
  SafetyCertificateOutline,
  StopOutline,
];
registerLocaleData(en);
registerLocaleData(localeSq, "sq");

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    NzIconModule.forRoot(icons),
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      fallbackLang: "en",
      loader: provideTranslateHttpLoader({
        prefix: "./assets/i18n/",
        suffix: ".json",
      }),
    }),
    TimeagoModule.forRoot(),
  ],
  providers: [
    provideHttpClient(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: "./assets/i18n/",
        suffix: ".json",
      }),
    }),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const http = httpLink.create({
        uri: `${environment.root}graphql`,
      });
      const wsLink = new GraphQLWsLink(
        createClient({
          url: environment.wsEndpoint,
          connectionParams: () => ({
            authToken: localStorage.getItem("ridy_admin_token"),
          }),
        }),
      );
      const basic = setContext(() => ({
        headers: {
          Accept: "charset=utf-8",
        },
      }));
      const auth = setContext(() => {
        return {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("ridy_admin_token")}`,
          },
        };
      });
      const defaultOptions: DefaultOptions = {
        watchQuery: {
          fetchPolicy: "no-cache",
          //errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: "no-cache",
          //errorPolicy: 'all',
        },
      };
      const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
          graphQLErrors.forEach(({ message, locations, path }) =>
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            ),
          );
        if (networkError) console.log(`[Network error]: ${networkError}`);
      });

      const link = ApolloLink.from([basic, auth, errorLink, http]);
      const splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          );
        },
        wsLink,
        link,
      );

      return {
        cache: new InMemoryCache({
          addTypename: false,
        }),
        link: splitLink,

        defaultOptions,
      };
    }),
    {
      provide: NZ_DATE_LOCALE,
      useFactory: () => {
        const lang: string =
          localStorage.getItem("lang") === "sq" ? "sq" : "en";
        switch (lang) {
          case "en":
            return enUS;

          case "es":
            return es;

          case "fr":
            return fr;

          case "de":
            return de;

          case "ar":
            return ar;

          case "hy":
            return hy;

          case "ko":
            return ko;

          case "ru":
            return ru;

          case "zh":
            return zhCN;

          case "ja":
            return ja;

          case "pt":
            return pt;

          case "ro":
            return ro;

          case "sq":
            return sq;

          default:
            return enUS;
        }
      },
    },
    {
      provide: NZ_I18N,
      //useValue: en_US,
      useFactory: (localId: string) => {
        const lang: string =
          localStorage.getItem("lang") === "sq" ? "sq" : "en";
        switch (lang) {
          case "en":
            return en_US;

          case "es":
            return es_ES;

          case "fr":
            return fr_FR;

          case "de":
            return de_DE;

          case "ar":
            return ar_EG;

          case "hy":
            return hy_AM;

          case "ko":
            return ko_KR;

          case "ru":
            return ru_RU;

          case "zh":
            return zh_CN;

          case "ja":
            return ja_JP;

          case "pt":
            return pt_PT;

          case "ro":
            return ro_RO;

          case "sq":
            return en_US;

          default:
            return en_US;
        }
      },
      deps: [LOCALE_ID],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private title: Title,
    private translator: TranslateService,
  ) {
    title.setTitle("Yoo Taxi Admin Panel");
  }
}
